import { useState, useEffect } from 'react';
import type { Task } from '@cowork-gen/core';
import { api } from '../api/client';

interface TaskListProps {
  onSelectTask: (task: Task) => void;
  selectedTaskId?: string;
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  running: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

export function TaskList({ onSelectTask, selectedTaskId }: TaskListProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 5000);
    return () => clearInterval(interval);
  }, [filter]);

  async function loadTasks() {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await api.getTasks(params);
      if (response.success && response.data) {
        setTasks(response.data);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel(task: Task) {
    if (!confirm(`Cancel task for ${task.storyId}?`)) return;

    try {
      await api.cancelTask(task.id);
      await loadTasks();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel task');
    }
  }

  async function handleDelete(task: Task) {
    if (!confirm(`Delete task for ${task.storyId}?`)) return;

    try {
      await api.deleteTask(task.id);
      await loadTasks();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete task');
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Tasks</h2>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="divide-y divide-gray-200">
        {tasks.length === 0 ? (
          <div className="p-4 text-gray-500 text-center">
            No tasks found
          </div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              className={`p-4 hover:bg-gray-50 cursor-pointer ${
                selectedTaskId === task.id ? 'bg-blue-50' : ''
              }`}
              onClick={() => onSelectTask(task)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 truncate">
                      {task.storyId}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        statusColors[task.status]
                      }`}
                    >
                      {task.status}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-500">
                    <span className="truncate">{task.repoId}</span>
                    <span className="mx-2">•</span>
                    <span>{task.branch}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-400">
                    Iterations: {task.iterations}/{task.maxIterations}
                    {task.agentId && (
                      <span className="ml-2">Agent: {task.agentId.slice(0, 8)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  {task.status === 'running' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancel(task);
                      }}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Cancel
                    </button>
                  )}
                  {(task.status === 'completed' ||
                    task.status === 'failed' ||
                    task.status === 'cancelled') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(task);
                      }}
                      className="text-xs text-gray-600 hover:text-gray-800"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
